import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import useIframeEnterKey from './useIframeEnterKey';
import { useNavigate } from 'react-router-dom';
import back_n from "../Tutorials/Image/back_n.gif";
import back_o from "../Tutorials/Image/back_o.gif";
import back_d from "../Tutorials/Image/back_d.gif";
import principle_n from "../Tutorials/Image/principle_n.gif";
import tutorials_n from "../Tutorials/Image/tutorials_n.gif";
import clue_n from "../Tutorials/Image/clue_n.gif";
import solution_n from "../Tutorials/Image/solution_n.gif";
import solution_o from "../Tutorials/Image/solution_o.gif";
import solution_d from "../Tutorials/Image/solution_d.gif";
import next_n from "../Tutorials/Image/next_n.gif";
import next_o from "../Tutorials/Image/next_o.gif";
import next_d from "../Tutorials/Image/next_d.gif";
import ok_n from "../Tutorials/Image/ok_n.gif";
import ok_o from "../Tutorials/Image/ok_o.gif";
import ok_d from "../Tutorials/Image/ok_d.gif";
import done_o from "../Tutorials/Image/done_o.gif";
import done_n from "../Tutorials/Image/done_n.gif";
import x_close_btn_n from "../Tutorials/Image/x_close_btn_n.png";
import x_close_btn_o from "../Tutorials/Image/x_close_btn_o.png";
import next_step_btn_n from "../Tutorials/Image/next_step_btn_n.png";
import next_step_btn_o from "../Tutorials/Image/next_step_btn_o.png";
import got_it_btn_n from "../Tutorials/Image/got_it_btn_n.png";
import got_it_btn_o from "../Tutorials/Image/got_it_btn_o.png";
import './Style/Question.css';
import Loading from "../../Common/Loading";
import { buildNextQuestionData, buildOptionOrder, buildShuffledQuestionsOrder, saveQuestionPerformance, QuestionPerformancePayload } from '../../services/questionApi';

export interface AnswerRef {
  getAnswer: () => {
    marks: number;
    noOfOptions: number;
    enteredCorrectAnswer: string;
    enteredWrongAnswer: string;
    enteredBlankAnswer: string;
    scoringMethod?: number | null;
    optOrder?: string;
  } | null;
}

export const normalizeTypeFAnswer = (value: string | undefined | null, isCaseSensitive = false): string => {
  if (value === undefined || value === null) return '';

  let normalized = value
    .replace(/[\u2018\u2019\u201A]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/ +/g, ' ')
    .trim();

  if (!isCaseSensitive) {
    normalized = normalized.toUpperCase();
  }

  return normalized;
};

export const formatTypeFRawAnswer = (value: string | undefined | null): string => {
  if (value === undefined || value === null) return '';

  return value
    .replace(/[\u2018\u2019\u201A]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/ +/g, ' ')
    .trim();
};

export const evaluateTypeFAnswer = (
  userAnswers: Record<string, string>,
  targets: Array<{ id?: string }>,
  correctAnswers: string[],
  isCaseSensitive = false,
) => {
  let correctCount = 0;
  let wrongCount = 0;
  const enteredCorrect: string[] = [];
  const enteredWrong: string[] = [];
  const enteredBlank: string[] = [];

  targets.forEach((element, index) => {
    const blankId = element?.id || `KSD_BLANK_${index}`;
    const rawUserAnswer = formatTypeFRawAnswer(userAnswers[blankId] ?? '');
    const userAnswer = normalizeTypeFAnswer(rawUserAnswer, isCaseSensitive);
    const correctAnswer = normalizeTypeFAnswer(correctAnswers[index] ?? '', isCaseSensitive);

    if (userAnswer === '') {
      wrongCount++;
      enteredBlank.push((index < 10 ? '0' + index : String(index)));
      return;
    }

    if (userAnswer === correctAnswer) {
      correctCount++;
      enteredCorrect.push((index < 10 ? '0' + index : String(index)) + rawUserAnswer);
      return;
    }

    wrongCount++;
    enteredWrong.push((index < 10 ? '0' + index : String(index)) + rawUserAnswer);
  });

  const allCorrect = correctCount === targets.length && wrongCount === 0;
  const marks = allCorrect ? 1 : 0;

  return {
    marks,
    noOfOptions: targets.length,
    enteredCorrectAnswer: enteredCorrect.join('~'),
    enteredWrongAnswer: enteredWrong.join('~'),
    enteredBlankAnswer: enteredBlank.join('~'),
    allCorrect,
  };
};

interface FQnsProps {
  lessonMeta?: any;
  questionHtml: string;
  currentPage: number;
  totalPages: number;
  goToNextPage?: (e: React.MouseEvent) => void;
  showAlert: (message: string) => void;
  correctAudio: string[];
  incorrectAudio: string[];
  assetsBaseUrl?: string;
  sessionId: number | null;
  isCompleting?: boolean;
  updateSessionPoints?: (pointsEarned: number) => void;
  hwid?: number | null;
  subject?: string;
  previewMode?: string | null; // NEW — "ANSWERS" | "SOLUTION" | undefined, from Question.tsx
}

const FQns: React.ForwardRefRenderFunction<AnswerRef, FQnsProps> = (props, ref) => {
  const { lessonMeta, questionHtml, currentPage, totalPages, goToNextPage, showAlert, correctAudio, incorrectAudio, assetsBaseUrl, sessionId, isCompleting, updateSessionPoints, hwid, subject, previewMode } = props;
  const navigate = useNavigate();
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const folderParam = params.get('folder');
  const lessonName = params.get('name');
  const diccode = params.get('diccode');
  const calledFrom = params.get('calledFrom');

  // NEW — same pattern as BQns/DQns. Single source of truth computed once
  // in React from the previewMode prop (which itself comes from the
  // ?PreviewMode= URL param via Question.tsx).
  const isViewLesson = params.get("ViewLesson") === "Y";
  const isAnswerMode = previewMode === "ANSWERS" || previewMode === "SOLUTION";
  const isSolutionMode = previewMode === "SOLUTION";
  const isPreview = isViewLesson && (isAnswerMode || isSolutionMode);

  const [backSrc, setBackSrc] = useState(back_n);
  const [nextSrc, setNextSrc] = useState(next_n);
  const [okSrc, setOkSrc] = useState(ok_n);
  const [doneSrc, setDoneSrc] = useState(done_n);
  const [solutionSrc, setSolutionSrc] = useState(solution_n);
  const [CloseBtnSrc, setCloseBtnSrc] = useState(x_close_btn_n);
  const [NextStepBtnSrc, setNextStepBtnSrc] = useState(next_step_btn_n);
  const [GotItBtnSrc, setGotItBtnSrc] = useState(got_it_btn_n);

  const [okClicked, setOkClicked] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const solnIframeRef = useRef<HTMLIFrameElement>(null);
  const [isNumberPad, setIsNumberPad] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const [currentQnMeta, setCurrentQnMeta] = useState<any | null>(null);
  const [showClueModal, setShowClueModal] = useState(false);
  const [solutionSeen, setSolutionSeen] = useState<string | number>('N');
  const [clueContent,
    setClueContent
  ] = useState('');
  const [isClueLoading,
    setIsClueLoading
  ] = useState(false);
  const [noOfClues, setNoOfClues] = useState(0);
  const [currentClueNo, setCurrentClueNo] = useState(0);
  const [clueSeen, setClueSeen] = useState(false);
  const clueIframeRef = useRef<HTMLIFrameElement>(null);
  const prcIframeRef = useRef<HTMLIFrameElement>(null);
  const [showPrcModal, setShowPrcModal] = useState(false);
  const [prcContent,
    setPrcContent
  ] = useState("");
  const [isPrcLoading,
    setIsPrcLoading
  ] = useState(false);
  const [prcSeen, setPrcSeen] = useState(false);
  const [principleList, setPrincipleList] = useState<string[]>([]);
  const [currentPrcIndex, setCurrentPrcIndex] = useState(0);
  const [showSolnModal, setShowSolnModal] = useState(false);
  const [isSolnLoading,
    setIsSolnLoading
  ] = useState(false);
  const [solnContent,
    setSolnContent
  ] = useState("");
  const [currentSolnNo, setCurrentSolnNo] = useState(0);
  const [noOfSteps, setNoOfSteps] = useState(0);
  const isInAssessment = window.location.href.includes('Assessment');

  useEffect(() => {
    setOkClicked(false);
    setAnswers({});
    setStartTime(new Date());
    setSolutionSeen('N');
    setClueSeen(false);
    setPrcSeen(false);
  }, [currentPage, questionHtml]);

  useEffect(() => {
    if (questionHtml) {
      const hasNumpad =
        /id=["']KSD_NUMPAD["']/i.test(questionHtml) ||
        /onclick\s*=\s*["'][^"']*insertDigit\s*\(/i.test(questionHtml);
      setIsNumberPad(hasNumpad);

      const infoMatch = questionHtml.match(/id="KSD_MW_Info"[^>]*value="([^"]*)"/i);
      let typeIsTyping = false;
      if (infoMatch && infoMatch[1]) {
        const parts = infoMatch[1].split('|');
        if (parts.length > 3 && parts[3] === '0' && !hasNumpad) {
          typeIsTyping = true;
        }
      }
      setIsTyping(typeIsTyping);
    }
  }, [questionHtml]);

  useEffect(() => {
    if (lessonMeta && Array.isArray(lessonMeta.qns) && currentPage <= lessonMeta.qns.length) {
      setCurrentQnMeta(lessonMeta.qns[currentPage - 1]);
    } else {
      setCurrentQnMeta(null);
    }
  }, [currentPage, lessonMeta]);

  const handleSolnClick = useCallback(async (autoExpand = false) => {
    if (!currentQnMeta || !lessonMeta) return;

    setShowSolnModal(true);
    setIsSolnLoading(true);
    setSolnContent("");
    setCurrentSolnNo(0);
    setNoOfSteps(0);

    const searchParams = new URLSearchParams(window.location.search);
    const folderParam = searchParams.get('folder');
    const countryParam = searchParams.get('country') || 'Australia';

    if (!folderParam) {
      showAlert("Could not determine the lesson folder.");
      setIsSolnLoading(false);
      setShowSolnModal(false);
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
      console.warn("Access token not found in localStorage.");
    }

    const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

    try {
      const response = await fetch(`${apiUrl}solution/`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          folder: folderParam,
          questionNo: currentQnMeta.QnNo,
          country: countryParam,
          autoExpand: autoExpand,
        }),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          showAlert("Authentication failed. Please log in again.");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSolnContent(data.solutionHtml || "");
      if (solutionSeen === 'N') {
        setSolutionSeen(1);
      }

    } catch (error) {
      console.error("Error fetching solution:", error);
      if (!(error instanceof Error && (error.message.includes("status: 401") || error.message.includes("status: 403")))) {
        showAlert("Failed to load the solution. Please try again.");
      }
      setShowSolnModal(false);
    } finally {
      setIsSolnLoading(false);
    }
  }, [currentQnMeta, lessonMeta, showAlert, solutionSeen]);

  // Keep the preview callback current without making opening a solution an
  // iframe-source dependency. Replacing srcdoc resets the student's feedback.
  const handleSolnClickRef = useRef(handleSolnClick);
  useEffect(() => {
    handleSolnClickRef.current = handleSolnClick;
  }, [handleSolnClick]);

  useImperativeHandle(ref, () => ({
    getAnswer: () => {
      const iframeDoc = iframeRef.current?.contentDocument;
      if (!iframeDoc) return null;

      const infoInput = iframeDoc.getElementById('KSD_MW_Info') as HTMLInputElement | null;
      const isCaseSensitive = infoInput?.value?.split('|')[6] === '1';

      const config = isNumberPad
        ? { userAnswers: answers, correctAnswerSelector: "#KSD_BW", splitter: '|', targetSelector: 'input[id^="KSD_BLANK_"]' }
        : { userAnswers: answers, correctAnswerSelector: "#KSD_ANS", splitter: ',', targetSelector: '.blank-box' };

      const targets = Array.from(iframeDoc.querySelectorAll(config.targetSelector)).sort((a, b) => parseInt(a.id.split('_').pop() || '0') - parseInt(b.id.split('_').pop() || '0')) as Array<{ id: string }>;
      const correctAnsEl = iframeDoc.querySelector(config.correctAnswerSelector) as HTMLInputElement | null;

      if (!correctAnsEl) {
        showAlert("Could not find answer key.");
        return null;
      }
      const correctAnswers = correctAnsEl.value.split(config.splitter);

      if (Object.values(config.userAnswers).filter(ans => ans?.trim() !== '').length < targets.length) {
        showAlert("Please answer the question.");
        return null;
      }

      const evaluation = evaluateTypeFAnswer(config.userAnswers, targets, correctAnswers, isCaseSensitive);

      return {
        marks: evaluation.marks,
        noOfOptions: evaluation.noOfOptions,
        enteredCorrectAnswer: evaluation.enteredCorrectAnswer,
        enteredWrongAnswer: evaluation.enteredWrongAnswer,
        enteredBlankAnswer: evaluation.enteredBlankAnswer,
        optOrder: buildOptionOrder(iframeRef.current, 'F'),
        scoringMethod: null
      };
    }
  }));

  const handleBackMouseOver = () => setBackSrc(back_o);
  const handleBackMouseOut = () => setBackSrc(back_n);
  const handleBackMouseDown = () => setBackSrc(back_d);
  const handleBackMouseUp = () => setBackSrc(back_o);
  const handleNextMouseOver = () => setNextSrc(next_o);
  const handleNextMouseOut = () => setNextSrc(next_n);
  const handleNextMouseDown = () => setNextSrc(next_d);
  const handleNextMouseUp = () => setNextSrc(next_o);
  const handleOkMouseOver = () => setOkSrc(ok_o);
  const handleOkMouseOut = () => setOkSrc(ok_n);
  const handleOkMouseDown = () => setOkSrc(ok_d);
  const handleOkMouseUp = () => setOkSrc(ok_o);
  const handleSolutionMouseOver = () => setSolutionSrc(solution_o);
  const handleSolutionMouseOut = () => setSolutionSrc(solution_n);
  const handleSolutionMouseDown = () => setSolutionSrc(solution_d);
  const handleCloseBtnMouseOver = () => setCloseBtnSrc(x_close_btn_o);
  const handleCloseBtnMouseOut = () => setCloseBtnSrc(x_close_btn_n);
  const handleNextStepBtnMouseOver = () => setNextStepBtnSrc(next_step_btn_o);
  const handleNextStepBtnMouseOut = () => setNextStepBtnSrc(next_step_btn_n);
  const handleGotItBtnMouseOver = () => setGotItBtnSrc(got_it_btn_o);
  const handleGotItBtnMouseOut = () => setGotItBtnSrc(got_it_btn_n);

  const fetchPrincipleContent = async (principleId: string, folderParam: string, countryParam: string) => {
    const accessToken = localStorage.getItem('access_token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

    const response = await fetch(`${apiUrl}principle/`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        folder: folderParam,
        questionNo: principleId,
        country: countryParam,
      }),
    });

    if (!response.ok) throw new Error("Failed");
    return await response.json();
  };

  const handlePrcClick = useCallback(async () => {
    if (!currentQnMeta || !lessonMeta) return;

    setShowPrcModal(true);
    setIsPrcLoading(true);
    setPrcContent("");

    const searchParams = new URLSearchParams(window.location.search);
    const folderParam = searchParams.get('folder');
    const countryParam = params.get('country') || 'Australia';

    if (!folderParam) {
      showAlert("Could not determine the lesson folder.");
      setIsPrcLoading(false);
      setShowPrcModal(false);
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
      console.warn("Access token not found in localStorage.");
    }

    const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

    try {
      const response = await fetch(`${apiUrl}principle/`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          folder: folderParam,
          questionNo: currentQnMeta.QnNo,
          country: countryParam,
        }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          setPrcContent("");
        } else {
          if (response.status === 401 || response.status === 403) {
            showAlert("Authentication failed. Please log in again.");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else {
        const data = await response.json();
        setPrcContent(data.principleHtml || "");
        setPrcSeen(true);
      }

    } catch (error) {
      console.error("Error fetching principle:", error);
      if (!(error instanceof Error && (error.message.includes("status: 401") || error.message.includes("status: 403") || error.message.includes("status: 404")))) {
        showAlert("Failed to load the principle. Please try again.");
      }
      setPrcContent("");
    } finally {
      setIsPrcLoading(false);
    }
  }, [currentQnMeta, lessonMeta, showAlert, params]);

  const handleClueClick = useCallback(async () => {
    if (!currentQnMeta || !lessonMeta) return;

    setShowClueModal(true);
    setIsClueLoading(true);
    setClueContent("");
    setCurrentClueNo(0);
    setNoOfClues(0);

    const searchParams = new URLSearchParams(window.location.search);
    const folderParam = searchParams.get('folder');
    const countryParam = params.get('country') || 'Australia';
    const varValuesParam = currentQnMeta?.VarValues || "";

    if (!folderParam) {
      showAlert("Could not determine the lesson folder.");
      setIsClueLoading(false);
      setShowClueModal(false);
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
      console.warn("Access token not found in localStorage.");
    }

    const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

    try {
      const response = await fetch(`${apiUrl}clue/`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          folder: folderParam,
          questionNo: currentQnMeta.QnNo,
          country: countryParam,
          varValues: varValuesParam,
        }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          setClueContent("");
        } else {
          if (response.status === 401 || response.status === 403) {
            showAlert("Authentication failed. Please log in again.");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else {
        const data = await response.json();
        setClueContent(data.clueHtml || "");
        setClueSeen(true);
      }

    } catch (error) {
      console.error("Error fetching clue:", error);
      if (!(error instanceof Error && (error.message.includes("status: 401") || error.message.includes("status: 403") || error.message.includes("status: 404")))) {
        showAlert("Failed to load the clue. Please try again.");
      }
      setClueContent("");
    } finally {
      setIsClueLoading(false);
    }
  }, [currentQnMeta, lessonMeta, showAlert, params]);

  const processAndInjectBridge = useCallback((html: string, showCorrectAnswer: boolean): string => {
    if (!html) return '';
    const infoMatch = html.match(/id="KSD_MW_Info"[^>]*value="([^"]*)"/i);
    const hasNumpad = html.includes('id="KSD_NUMPAD"');
    let isTypingLocal = false;

    if (infoMatch && infoMatch[1]) {
      const parts = infoMatch[1].split('|');
      if (parts.length > 3 && parts[3] === '0' && !hasNumpad) {
        isTypingLocal = true;
      }
    }

    let processedHtml = html.trim().startsWith('\ufeff') ? html.trim().substring(1) : html.trim();
    processedHtml = processedHtml.replace(/http:\/\//gi, "https://");
    // Remove ALL legacy inline handlers that depend on the old VB/ASPX JS.
    // This is intentionally also done here (not only in injectNumberPadBridge)
    // because React state updates are asynchronous. During a question change,
    // isNumberPad can briefly still contain the previous question's value.
    processedHtml = processedHtml.replace(
      /on(mousemove|mouseup|mousedown|keypress|change|load)\s*=\s*(['"])(?:(?!\2).)*\2/gi,
      ""
    );

    // Never leave the legacy number-pad handler inside srcdoc.
    // Otherwise the browser executes onclick="insertDigit(...)" directly.
    processedHtml = processedHtml.replace(
      /onclick\s*=\s*(['"])\s*insertDigit\(\s*(['"])(.*?)\2\s*\)\s*\1/gi,
      (_match, _attrQuote, _argQuote, digit) => `data-insert-digit="${digit}"`
    );

    processedHtml = processedHtml.replace(/<script[\s\S]*?<\/script>/gi, "");

    const bwMatch = processedHtml.match(/<input[^>]*id="KSD_BW"[^>]*value="([^"]*)"/i);
    if (bwMatch) {
      const correctAnswers = bwMatch[1].split('|').join(',');
      const answerKeyInputHtml = `<input type="hidden" id="KSD_ANS" value="${correctAnswers}" />`;
      processedHtml = processedHtml.replace('</body>', answerKeyInputHtml + '</body>');
    }

    if (isTypingLocal) {
      processedHtml = processedHtml.replace(/(<input[^>]*id="KSD_BLANK_\d+"[^>]*?)(\s*on\w+="[^"]*")+/gi, '$1');

      const bridge = `
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
              p { margin: 0; padding: 0; }
              table { border-collapse: separate; }
              /*
               * Legacy F markup assigns percentage widths to the question
               * and answer cells, while the answer list itself has a fixed
               * 280px width.  A longer prompt can therefore make the
               * browser move the answer cell below the question (as seen on
               * question 5).  Keep the answer panel at a known width and let
               * only the question cell consume the remaining space.
               */
              #KSD_TABLE { table-layout: fixed; }
              #KSD_TABLE > tbody > tr > td:first-child { width: auto !important; min-width: 0; }
              #KSD_TABLE > tbody > tr > td:last-child { width: 300px !important; min-width: 300px; }
              #KSD_TABLE > tbody > tr > td:first-child table { max-width: 100%; }
              #KSD_TABLE > tbody > tr > td:first-child img { max-width: 100%; height: auto; }
              #KSD_WORDS2FILLUP {
                  width: 280px !important;
                  margin-left: auto !important;
                  margin-right: auto !important;
                }
              
              input[id^="KSD_BLANK_"] {
                  border: none !important;
                  border-bottom: 4px solid red !important;
                  background-color: transparent !important;
                  background-image: none !important;
                  text-align: center !important;
                  
                  font-family: Verdana, Arial, sans-serif !important;
                  font-size: 18pt !important;
                  color: #017ebc !important; 
                  font-weight: normal !important;
                  
                  outline: none !important;
                  min-width: 50px;
                  padding: 0 5px;
                  border-radius: 0 !important;
                  line-height: normal !important;
              }
              
              input[id^="KSD_BLANK_"]:focus {
                  border: none !important;
                  border-bottom: 4px solid red !important;
                  outline: 2px solid black !important;
                  border-radius: 2px !important;
                  background-color: transparent !important;
                  background-image: none !important;
                  box-shadow: none !important;
              }

              input[id^="KSD_BLANK_"][readonly] {
                  border-bottom: 4px solid green !important;
                  color: green !important;
                  cursor: default;
              }
            </style>
            <script>
              document.addEventListener('DOMContentLoaded', function() {
                const answers = {};
                // The source has all option cells in a single <tr>.  Split
                // them into real rows so table recovery is deterministic at
                // every viewport width.
                const optionTable = document.getElementById('KSD_WORDS2FILLUP');
                if (optionTable) {
                  const options = Array.from(optionTable.querySelectorAll('td[id^="KSD_FIGTD_"]'));
                  const optionRow = options[0] && options[0].parentElement;
                  if (optionRow && optionRow.tagName === 'TR') {
                    options.forEach(function(option) {
                      const row = document.createElement('tr');
                      option.colSpan = 2;
                      row.appendChild(option);
                      optionRow.parentElement.insertBefore(row, optionRow);
                    });
                    optionRow.remove();
                  }
                }
                function postAnswers() { parent.postMessage({ type: "el-fillin-answers", value: answers }, "*"); }

                // Baked in from React. No runtime window.parent lookups.
                const showCorrectAnswer = ${JSON.stringify(showCorrectAnswer)};
                let correctAnswers = [];
                if (showCorrectAnswer) {
                  const ansEl = document.getElementById('KSD_ANS');
                  if (ansEl && ansEl.value) correctAnswers = ansEl.value.split(',');
                }
                
                const inputs = Array.from(document.querySelectorAll('input[id^="KSD_BLANK_"]'))
                  .sort(function(a, b) {
                    return parseInt(a.id.split('_').pop() || '0') - parseInt(b.id.split('_').pop() || '0');
                  });

                inputs.forEach(function(input) {
                    const size = parseInt(input.getAttribute('size') || '', 10);
                    if (Number.isInteger(size) && size > 0) input.maxLength = size;
                });

                function focusNextIfFilled(input, idx) {
                    const advanceAfter = parseInt(input.getAttribute('size') || '', 10);
                    if (idx < inputs.length - 1 && Number.isInteger(advanceAfter) && advanceAfter > 0 && input.value.length >= advanceAfter) {
                        inputs[idx + 1].focus();
                    }
                }

                inputs.forEach(function(input, idx) {
                    if(input.value) answers[input.id] = input.value;
                    
                    input.setAttribute('autocomplete', 'off');
                    
                    const adjustWidth = () => {
                        const len = input.value.length;
                        input.style.width = (len > 3 ? len + 1 : 4) + 'ch';
                    };
                    adjustWidth();

                    if (showCorrectAnswer) {
                        const correctVal = (correctAnswers[idx] || '').trim();
                        input.value = correctVal;
                        input.readOnly = true;
                        input.tabIndex = -1;
                        answers[input.id] = correctVal;
                        adjustWidth();

                        const img = document.createElement('img');
                        img.src = "https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsCorrect.GIF";
                        img.alt = "Correct";
                        img.style.verticalAlign = "middle";
                        img.style.marginLeft = "6px";
                        input.insertAdjacentElement('afterend', img);
                    } else {
                        input.addEventListener('input', (e) => {
                            answers[e.target.id] = e.target.value;
                            adjustWidth();
                            postAnswers();
                            focusNextIfFilled(input, idx);
                        });
                    }
                });

                if (showCorrectAnswer) postAnswers();
              });
            </script>`;

      return processedHtml.replace("</body>", bridge + "</body>");

    } else {
      processedHtml = processedHtml.replace(/<input[^>]*id="KSD_BLANK_(\d+)"[^>]*>/gi, '<span id="KSD_BLANK_$1" class="blank-box"></span>');

      const bridge = `
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 0; overflow: hidden; }
              p { margin: 0; padding: 0; }
              table { border-collapse: separate; }
              /* See the typing bridge above: reserve the fixed answer
                 column so long F prompts cannot wrap it below the card. */
              #KSD_TABLE { table-layout: fixed; }
              #KSD_TABLE > tbody > tr > td:first-child { width: auto !important; min-width: 0; }
              #KSD_TABLE > tbody > tr > td:last-child { width: 300px !important; min-width: 300px; }
              #KSD_TABLE > tbody > tr > td:first-child table { max-width: 100%; }
              #KSD_TABLE > tbody > tr > td:first-child img { max-width: 100%; height: auto; }
              #KSD_WORDS2FILLUP {
                  width: 280px !important;
                  margin-left: auto !important;
                  margin-right: auto !important;
                }
              .blank-box { 
                  display: inline-flex; 
                  flex-direction: row; 
                  justify-content: center; 
                  align-items: center; 
                  min-width: 30px; 
                  width: auto; 
                  height: 30px; 
                  border: 2px solid black !important; 
                  font-family: Verdana, sans-serif; 
                  background-color: white; 
                  cursor: pointer;
                  font-size: 18pt; 
                  vertical-align: middle; 
                  padding: 2px 5px; 
                  color: #017ebc;
              }
              .blank-box.selected { border: 4px solid red !important; } 
              .blank-box.filled { }
              .blank-box.preview-correct {
                  border: 2px solid green !important;
                  color: green;
                  cursor: default;
              }
            </style>
            <script>
              document.addEventListener('DOMContentLoaded', function() {
                try {
                  let activeBlank = null; let currentSelectedOption = null; const answers = {};
                  let normalBgUrl = ''; let selectedBgUrl = '';
                  // The legacy HTML puts all answer cells in one row.  Move
                  // each one into its own row before binding choice handlers.
                  const optionTable = document.getElementById('KSD_WORDS2FILLUP');
                  if (optionTable) {
                    const options = Array.from(optionTable.querySelectorAll('td[id^="KSD_FIGTD_"]'));
                    const optionRow = options[0] && options[0].parentElement;
                    if (optionRow && optionRow.tagName === 'TR') {
                      options.forEach(function(option) {
                        const row = document.createElement('tr');
                        option.colSpan = 2;
                        row.appendChild(option);
                        optionRow.parentElement.insertBefore(row, optionRow);
                      });
                      optionRow.remove();
                    }
                  }
                  const firstOption = document.querySelector('td[data-normal-bg]');
                  if (firstOption) { normalBgUrl = firstOption.getAttribute('data-normal-bg'); selectedBgUrl = firstOption.getAttribute('data-selected-bg'); } 

                  function postAnswers() { parent.postMessage({ type: "el-fillin-answers", value: answers }, "*"); }
                  
                  function updateBlankState(el) {
                    if (!el) return;
                    const len = el.textContent ? el.textContent.trim().length : 0;
                    if (len > 0) {
                        el.style.width = 'auto';
                        el.style.minWidth = 'auto';
                        el.classList.add('filled');
                    } else {
                        el.style.width = '50px';
                        el.classList.remove('filled');
                    }
                  }

                  const blanks = Array.from(document.querySelectorAll('.blank-box')).sort((a, b) => parseInt(a.id.split('_').pop() || '0') - parseInt(b.id.split('_').pop() || '0'));

                  // Baked in from React.
                  const showCorrectAnswer = ${JSON.stringify(showCorrectAnswer)};

                  if (showCorrectAnswer) {
                    let correctAnswersList = [];
                    const ansEl = document.getElementById('KSD_ANS');
                    if (ansEl && ansEl.value) correctAnswersList = ansEl.value.split(',');

                    blanks.forEach(function(blank, idx) {
                      const correctVal = (correctAnswersList[idx] || '').trim();
                      blank.textContent = correctVal;
                      updateBlankState(blank);
                      blank.classList.add('preview-correct');
                      answers[blank.id] = correctVal;

                      const img = document.createElement('img');
                      img.src = "https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsCorrect.GIF";
                      img.alt = "Correct";
                      img.style.verticalAlign = "middle";
                      img.style.marginLeft = "4px";
                      blank.insertAdjacentElement('afterend', img);
                    });

                    document.querySelectorAll('td[id^="KSD_FIGTD_"]').forEach(function(opt) {
                      opt.style.pointerEvents = 'none';
                      opt.style.cursor = 'default';
                    });

                    postAnswers();

                  } else {
                    function setActiveBlank(blank) { if (activeBlank) activeBlank.classList.remove('selected'); activeBlank = blank; if (activeBlank) activeBlank.classList.add('selected'); }
                    
                    if (blanks.length > 0) setActiveBlank(blanks[0]);
                    
                    blanks.forEach(blank => { 
                        updateBlankState(blank); 
                        blank.addEventListener('click', () => setActiveBlank(blank)); 
                    });

                    document.querySelectorAll('td[id^="KSD_FIGTD_"]').forEach(option => {
                        option.addEventListener('click', () => {
                            if (currentSelectedOption && currentSelectedOption !== option) { 
                                currentSelectedOption.style.backgroundImage = 'url(' + normalBgUrl + ')'; 
                            }
                            option.style.backgroundImage = 'url(' + selectedBgUrl + ')'; 
                            currentSelectedOption = option;
                            
                            if (activeBlank) {
                                const newAnswerValue = option.textContent.trim(); 
                                activeBlank.textContent = newAnswerValue;
                                
                                updateBlankState(activeBlank);
                                answers[activeBlank.id] = newAnswerValue; 
                                postAnswers();

                                const currentIndex = blanks.indexOf(activeBlank); 
                                if (currentIndex < blanks.length - 1) {
                                    setActiveBlank(blanks[currentIndex + 1]);
                                }
                            }
                        });
                    });
                  }
                } catch(e) { console.error("Bridge Error:", e); }
              });
            </script>`;
      return processedHtml.replace("</body>", bridge + "</body>");
    }
  }, []);

  const injectNumberPadBridge = useCallback((html: string, showCorrectAnswer: boolean): string => {
    let safeHtml = html.replace(/http:\/\//gi, "https://");
    let cleaned = safeHtml
      .replace(/onmousemove="[^"]*"/gi, "")
      // Legacy F question HTML calls hideF() from body onmouseup.
      // EL_F.js is not loaded inside the srcdoc iframe, so that inline
      // handler causes "ReferenceError: hideF is not defined".
      // Remove only this legacy handler; the React bridge handles the
      // number-pad/input behaviour.
      .replace(/onmouseup\s*=\s*(['"])(?:(?!\1).)*\1/gi, "")
      // Do not leave inline onclick="insertDigit(...)" handlers in srcdoc.
      // Inline handlers depend on a global function inside the iframe and
      // can execute before/without the injected bridge being available.
      // Convert them to data attributes and bind real DOM listeners below.
      .replace(
        /onclick\s*=\s*(['"])\s*insertDigit\(\s*(['"])(.*?)\2\s*\)\s*\1/gi,
        (_match, _attrQuote, _argQuote, digit) => `data-insert-digit="${digit}"`
      )
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/(<td[^>]*id="KSD_TD_[^"]*"[^>]*)\s+style="[^"]*"/gi, "$1");

    // Final hard guarantee: no executable legacy insertDigit onclick may survive.
    // The replacement above converts known handlers to data attributes; this
    // removes any unusual formatting variant that did not match it.
    cleaned = cleaned.replace(
      /\s+onclick\s*=\s*(['"])[^'"]*insertDigit\s*\([^)]*\)\s*\1/gi,
      ""
    );
    const bridge = `
      <style>
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: hidden !important;
          overflow-y: hidden !important;
          box-sizing: border-box !important;
        }

        #KSD_TABLE {
          width: calc(100% - 20px) !important;
          max-width: calc(100% - 20px) !important;
          margin-right: 20px !important;
          display: table !important;
          table-layout: fixed !important;
          box-sizing: border-box !important;
        }

        #KSD_TABLE > tbody > tr > td {
          box-sizing: border-box !important;
        }

        #KSD_TABLE > tbody {
          display: table-row-group !important;
        }

        #KSD_TABLE > tbody > tr {
          display: table-row !important;
        }

        #KSD_TABLE > tbody > tr > td:first-child {
          width: calc(100% - 330px) !important;
          min-width: 0 !important;
          text-align: center !important;
          vertical-align: top !important;
        }

        #KSD_TABLE > tbody > tr > td#KSD_DIVIDERBAR {
          width: 10px !important;
          min-width: 10px !important;
          max-width: 10px !important;
        }

        #KSD_TABLE > tbody > tr > td#KSD_NUMPAD {
          width: 350px !important;
          min-width: 350px !important;
          max-width: 350px !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          text-align: left !important;
          vertical-align: top !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
        }

        #KSD_NUMPAD > table {
          width: 310px !important;
          max-width: 310px !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          box-sizing: border-box !important;
        }

        #KSD_TABLE > tbody > tr > td:first-child table[name="TableQn"] {
          width: 100% !important;
          margin-left: auto !important;
          margin-right: auto !important;
        }

        #KSD_TABLE > tbody > tr > td:first-child table[name="TableQn"] p {
          text-align: center !important;
        }
        p { margin: 0; padding: 0; }

        font {
          color: black;
        }

        input[id^="KSD_BLANK_"] {
          caret-color: #017EBC; 
          border: 2px solid rgb(56, 77, 100) !important;
          font-weight: normal !important;
          font-family: Verdana, sans-serif !important;
          text-align: center !important;
          color: #017EBC !important;
          font-size: 18pt !important; 
          min-width: 5ch;
        }
        
        input[id^="KSD_BLANK_"].active-input {
          outline: none;
          border: 4px solid red !important;
        }

        input[id^="KSD_BLANK_"]:focus {
          outline: 1px solid black !important;
          border: 4px solid red !important;
          border-radius: 5px;
        }

        input[id^="KSD_BLANK_"][readonly] {
          border: 2px solid green !important;
          color: green !important;
        }
        
        table img[data-insert-digit] { 
          cursor: pointer; 
          user-select: none;
          -webkit-user-select: none;
        }
      </style>
      <script>
        // The legacy F HTML contains inline insertDigit(...) calls.
        // Those calls are converted to data-insert-digit attributes before
        // this bridge is injected. We deliberately do NOT expose a global
        // insertDigit() function: real event listeners are safer and avoid
        // srcdoc/global-scope timing issues.
        document.addEventListener('DOMContentLoaded', function() {
          try {
            try {
              const numPadContainer = document.getElementById('KSD_NUMPAD');
              
              if (numPadContainer) {
                const innerTable = numPadContainer.querySelector('table');
                
                if (innerTable) {
                  innerTable.style.marginTop = '0px';
                  innerTable.style.margin = '0px';
                  innerTable.style.borderSpacing = '0px';
                  innerTable.setAttribute('cellpadding', '0');

                  if (innerTable.rows.length > 0) {
                    const firstButtonRow = innerTable.rows[0];
                    const numCols = firstButtonRow.cells.length; 

                    const labelRow = innerTable.insertRow(0); 
                    
                    const labelCell = labelRow.insertCell(0);
                    labelCell.textContent = 'Number pad:';

                    labelCell.style.paddingBottom = '10px'; 
                    labelCell.style.fontFamily = 'arial'; 
                    labelCell.style.fontSize = '24pt'; 
                    labelCell.style.color = '#3c5168'; 
                    labelCell.style.fontWeight = 'normal'; 

                    if (numCols > 1) {
                      labelCell.colSpan = numCols;
                    }
                  }
                }
              } else {
                const numPadButton = document.querySelector('[data-insert-digit]');
                if (numPadButton) {
                  const innerTable = numPadButton.closest('table');
                  if (innerTable) {
                    innerTable.style.margin = '0px';
                    innerTable.style.marginTop = '0px';
                    innerTable.style.borderSpacing = '0px';
                    innerTable.setAttribute('cellpadding', '0');

                    if (innerTable.rows.length > 0) {
                      const firstButtonRow = innerTable.rows[0];
                      const numCols = firstButtonRow.cells.length;
                      const labelRow = innerTable.insertRow(0);
                      const labelCell = labelRow.insertCell(0);
                      labelCell.textContent = 'Number pad:';
                      labelCell.style.textAlign = 'left';
                      labelCell.style.paddingBottom = '10px';
                      labelCell.style.fontFamily = 'arial';
                      labelCell.style.fontSize = '24pt';
                      labelCell.style.color = '#3c5168';
                      labelCell.style.fontWeight = 'normal';
                      if (numCols > 1) {
                        labelCell.colSpan = numCols;
                      }
                    }
                  }
                }
              }
            } catch (e) {
              console.error('Error adding number pad label:', e);
            }

            // Baked in from React.
            const showCorrectAnswer = ${JSON.stringify(showCorrectAnswer)};
            let correctAnswers = [];
            if (showCorrectAnswer) {
              const bwEl = document.getElementById('KSD_BW');
              if (bwEl && bwEl.value) correctAnswers = bwEl.value.split('|');
            }

            const inputs = Array.from(document.querySelectorAll('input[id^="KSD_BLANK_"]'))
              .sort(function(a, b) {
                return parseInt(a.id.split('_').pop() || '0') - parseInt(b.id.split('_').pop() || '0');
              });

            inputs.forEach(function(input) {
              const size = parseInt(input.getAttribute('size') || '', 10);
              if (Number.isInteger(size) && size > 0) input.maxLength = size;
            });

            function setActive(el) {
                if (!el) return;
                activeInput = el;
                inputs.forEach(i => i.classList.remove('active-input'));
                el.classList.add('active-input');
            }

            // Robust F number-pad handling:
            // the legacy inline onclick handlers have been removed above.
            // Bind directly to the number-pad images so there is no
            // dependency on a global function such as insertDigit().
            const numberPadButtons = Array.from(
              document.querySelectorAll('[data-insert-digit]')
            );

            numberPadButtons.forEach(function(button) {
              button.addEventListener('click', function(event) {
                event.preventDefault();
                event.stopPropagation();

                try {
                  if (showCorrectAnswer) return;

                  if (!activeInput || !document.body.contains(activeInput)) {
                    if (inputs.length > 0) {
                      setActive(inputs[0]);
                    }
                  }

                  if (!activeInput) return;

                  const digit = button.getAttribute('data-insert-digit') || '';
                  if (!digit) return;

                  if (digit === '←') {
                    activeInput.value = activeInput.value.slice(0, -1);
                  } else {
                    if (activeInput.maxLength > 0 && activeInput.value.length >= activeInput.maxLength) return;
                    activeInput.value += digit;
                  }

                  activeInput.dispatchEvent(
                    new Event('input', { bubbles: true })
                  );

                  activeInput.focus();
                } catch (e) {
                  console.error('Number Pad Input Error:', e);
                }
              });
            });

            if (showCorrectAnswer) {
              inputs.forEach(function(input, idx) {
                const correctVal = (correctAnswers[idx] || '').trim();
                input.value = correctVal;
                input.readOnly = true;
                input.tabIndex = -1;
                input.style.width = Math.max(2, correctVal.length) + 'ch';

                const img = document.createElement('img');
                img.src = "https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsCorrect.GIF";
                img.alt = "Correct";
                img.style.verticalAlign = "middle";
                img.style.marginLeft = "6px";
                input.insertAdjacentElement('afterend', img);
              });

              const allAnswers = {};
              inputs.forEach(inp => { allAnswers[inp.id] = inp.value; });
              parent.postMessage({ type: 'el-fillin-answers', value: allAnswers }, '*');

              const numPad = document.getElementById('KSD_NUMPAD');
              if (numPad) numPad.style.display = 'none';

            } else {
              if (inputs.length > 0) {
                setActive(inputs[0]);
                inputs[0].focus();
              }

              inputs.forEach(input => {
                input.style.width = Math.max(2, input.value.length) + 'ch';
                const advanceAfter = parseInt(input.getAttribute('size') || '', 10);
                
                input.addEventListener('focus', function() { 
                    setActive(this);
                });
                
                input.addEventListener('input', function() {
                  this.value = this.value.replace(/[^0-9 \\-+*/().]/g, '');
                  if (this.maxLength > 0) this.value = this.value.slice(0, this.maxLength);
                  const allAnswers = {};
                  inputs.forEach(inp => { allAnswers[inp.id] = inp.value; });
                  parent.postMessage({ type: 'el-fillin-answers', value: allAnswers }, '*');
                  this.style.width = Math.max(2, this.value.length) + 'ch';

                  const currentIndex = inputs.indexOf(this);
                  if (Number.isInteger(advanceAfter) && advanceAfter > 0 && this.value.length >= advanceAfter && currentIndex < inputs.length - 1) {
                    const nextInput = inputs[currentIndex + 1];
                    if (nextInput) {
                      setActive(nextInput);
                      nextInput.focus();
                    }
                  }
                });
              });
              

            }

          } catch(e) { console.error("Number Pad Bridge Error:", e); }
        });
      </script>
    `;
    return cleaned.includes('</body>') ? cleaned.replace('</body>', bridge + '</body>') : cleaned + bridge;
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && questionHtml) {
      // Determine the mode from THIS question, synchronously.
      // Do not rely on isNumberPad state here: setState is asynchronous and
      // can otherwise cause one question to be injected using the previous
      // question's bridge.
      const currentQuestionHasNumberPad =
        /id=["']KSD_NUMPAD["']/i.test(questionHtml) ||
        /onclick\s*=\s*["'][^"']*insertDigit\s*\(/i.test(questionHtml);

      // Set the load handler before assigning srcdoc so the handler belongs
      // to the current question load.
      iframe.onload = () => {
        const iframeDoc = iframe.contentDocument;
        const questionTable = iframeDoc?.getElementById('KSD_TABLE') as HTMLTableElement | null;
        const questionRow = questionTable?.rows[0];
        const wordsTable = iframeDoc?.getElementById('KSD_WORDS2FILLUP') as HTMLTableElement | null;

        // Some F source files (including "This music is ...") contain one
        // extra closing </table></tr>. The browser consequently parses the
        // answer table as a sibling of KSD_TABLE rather than as its third
        // cell. Restore that missing table structure before styling it.
        if (
          questionTable &&
          questionRow &&
          wordsTable &&
          !questionTable.contains(wordsTable)
        ) {
          const dividerCell = iframeDoc!.createElement('td');
          dividerCell.id = 'KSD_DIVIDERBAR';
          dividerCell.style.paddingRight = '10px';

          const answerCell = iframeDoc!.createElement('td');
          answerCell.setAttribute('align', 'right');
          answerCell.setAttribute('valign', 'top');
          answerCell.appendChild(wordsTable);

          questionRow.append(dividerCell, answerCell);
        }

        // ONLY structured F questions use KSD_WORDS2FILLUP.
        // Do not touch the layout of F number-pad questions.
        const isStructuredF = !!wordsTable;

        if (isStructuredF && questionTable && questionRow && questionRow.cells.length >= 3) {
          const [questionCell, dividerCell, answerCell] = Array.from(questionRow.cells);
          const tableBody = questionTable.tBodies[0];

          // Repair/layout only for structured F (question | divider | options).
          questionTable.style.display = 'block';
          questionTable.style.width = 'calc(100% - 20px)';
          questionTable.style.maxWidth = 'calc(100% - 20px)';
          questionTable.style.marginRight = '20px';
          questionTable.style.tableLayout = 'fixed';
          if (tableBody) tableBody.style.display = 'block';

          questionRow.style.display = 'grid';
          questionRow.style.gridTemplateColumns = 'minmax(0, 1fr) 10px 300px';
          questionRow.style.width = '100%';

          questionCell.style.display = 'block';
          questionCell.style.width = 'auto';
          questionCell.style.minWidth = '0';

          dividerCell.style.display = 'block';
          dividerCell.style.width = '10px';
          dividerCell.style.paddingRight = '10px';

          answerCell.style.display = 'block';
          answerCell.style.width = '300px';
          answerCell.style.minWidth = '300px';

          // Keep options in their existing right-hand column.
          answerCell.style.textAlign = 'center';
          wordsTable.style.width = '280px';
          wordsTable.style.marginLeft = 'auto';
          wordsTable.style.marginRight = 'auto';

          // Center ONLY the question content for structured F.
          questionCell.style.textAlign = 'center';

          const instructionTable =
            questionCell.querySelector('table[name="TableQn"]') ||
            questionCell.querySelector('table');

          if (instructionTable instanceof HTMLElement) {
            instructionTable.style.marginLeft = 'auto';
            instructionTable.style.marginRight = 'auto';
            instructionTable.style.textAlign = 'center';
          }

          // Center the separate legacy font containing the blanks/sequence.
          const sequenceFont = Array.from(
            questionCell.querySelectorAll('font')
          ).find((font) => font.querySelector('.blank-box'));

          if (sequenceFont instanceof HTMLElement) {
            sequenceFont.style.display = 'block';
            sequenceFont.style.width = '100%';
            sequenceFont.style.marginLeft = '0';
            sequenceFont.style.marginRight = '0';
            sequenceFont.style.textAlign = 'center';
          }
        }

        // NUMBER-PAD F QUESTIONS: keep the number pad fixed on the RIGHT and
        // center ONLY the question content in the remaining LEFT area.
        // This branch deliberately does not run for KSD_WORDS2FILLUP questions.
        if (!isStructuredF && currentQuestionHasNumberPad && questionTable && questionRow && questionRow.cells.length >= 3) {
          const [questionCell, dividerCell, numberPadCell] = Array.from(questionRow.cells);

          questionTable.style.display = 'table';
          questionTable.style.width = '100%';
          questionTable.style.tableLayout = 'fixed';

          questionRow.style.display = 'table-row';
          questionRow.style.width = '100%';

          questionCell.style.display = 'table-cell';
          questionCell.style.width = 'calc(100% - 330px)';
          questionCell.style.minWidth = '0';
          questionCell.style.textAlign = 'center';
          questionCell.style.verticalAlign = 'top';

          dividerCell.style.display = 'table-cell';
          dividerCell.style.width = '10px';
          dividerCell.style.minWidth = '10px';
          dividerCell.style.maxWidth = '10px';

          numberPadCell.style.display = 'table-cell';
          numberPadCell.style.width = '320px';
          numberPadCell.style.minWidth = '320px';
          numberPadCell.style.maxWidth = '320px';
          numberPadCell.style.textAlign = 'left';
          numberPadCell.style.verticalAlign = 'top';

          const numberPadQuestionTable =
            questionCell.querySelector('table[name="TableQn"]') ||
            questionCell.querySelector('table');

          if (numberPadQuestionTable instanceof HTMLElement) {
            numberPadQuestionTable.style.width = '100%';
            numberPadQuestionTable.style.marginLeft = 'auto';
            numberPadQuestionTable.style.marginRight = 'auto';
            numberPadQuestionTable.style.textAlign = 'center';
          }

          questionCell.querySelectorAll('p').forEach((p) => {
            (p as HTMLElement).style.textAlign = 'center';
          });
        }

        if (isPreview) {
          setOkClicked(true);
          if (isSolutionMode) {
            handleSolnClickRef.current(true);
          }
        }
      };

      iframe.srcdoc = currentQuestionHasNumberPad
        ? injectNumberPadBridge(questionHtml, isPreview)
        : processAndInjectBridge(questionHtml, isPreview);

      // Admin preview is handled by the onload handler above.
      // Real students (isPreview=false) never enter the preview path.
    }
  }, [questionHtml, isNumberPad, processAndInjectBridge, isPreview, isSolutionMode, injectNumberPadBridge]);

  const playRandomSound = useCallback(async (soundFiles: string[], folder: 'Correct' | 'Incorrect') => {
    const baseUrl = assetsBaseUrl || "https://www.elearnoncloud.com/Storage/assets/";
    if (!soundFiles || soundFiles.length === 0) return;
    const fileName = soundFiles[Math.floor(Math.random() * soundFiles.length)];
    const audioUrl = `${baseUrl}Audio/Question/${folder}/${fileName}`;
    const audio = new Audio(audioUrl);
    try { await audio.play(); } catch (error) { console.error(`Error playing audio from ${audioUrl}:`, error); }
  }, [assetsBaseUrl]);

  const handleDbUpdate = useCallback((payload: QuestionPerformancePayload) => {
    // Never persist performance/points data for admin preview renders.
    // handleOkClick is never actually invoked in preview (OK button is
    // hidden once okClicked is set programmatically above), but this
    // guard protects against any edge case.
    if (isPreview) {
      return Promise.resolve(null as any);
    }
    return saveQuestionPerformance(payload)
      .then((data) => {
        if (isCompleting && updateSessionPoints) {
          updateSessionPoints(data.pointsEarned || 0);
        }
        return data;
      })
      .catch((error) => {
        console.error("Save Performance Error:", error);
        throw error;
      });
  }, [isCompleting, updateSessionPoints, isPreview]);

  const handleOkClick = useCallback(async (e: React.MouseEvent<any>) => {
    e.preventDefault();
    if (okClicked || isSubmitting) return;

    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) return;
    const iframeDoc = iframe.contentDocument;

    const infoInput = iframeDoc.getElementById('KSD_MW_Info') as HTMLInputElement | null;
    const isCaseSensitive = infoInput?.value?.split('|')[6] === '1';

    const config = (isNumberPad || isTyping)
      ? {
        userAnswers: answers,
        correctAnswerSelector: "#KSD_BW",
        splitter: '|',
        targetSelector: 'input[id^="KSD_BLANK_"]',
        isInput: true
      }
      : {
        userAnswers: answers,
        correctAnswerSelector: "#KSD_ANS",
        splitter: ',',
        targetSelector: '.blank-box',
        isInput: false
      };

    const targets = Array.from(iframeDoc.querySelectorAll(config.targetSelector)).sort((a, b) => parseInt(a.id.split('_').pop() || '0') - parseInt(b.id.split('_').pop() || '0')) as Array<{ id: string }>;
    const correctAnsEl = iframeDoc.querySelector(config.correctAnswerSelector) as HTMLInputElement | null;

    if (!correctAnsEl) {
      showAlert("Could not find answer key.");
      return;
    }
    const correctAnswers = correctAnsEl.value.split(config.splitter);

    if (Object.values(config.userAnswers).filter(ans => ans?.trim() !== '').length < targets.length) {
      showAlert("Please answer the question.");
      return;
    }

    const evaluation = evaluateTypeFAnswer(config.userAnswers, targets, correctAnswers, isCaseSensitive);
    const allCorrect = evaluation.allCorrect;
    const marks = evaluation.marks;
    const enteredCorrect = evaluation.enteredCorrectAnswer ? evaluation.enteredCorrectAnswer.split('~') : [];
    const enteredWrong = evaluation.enteredWrongAnswer ? evaluation.enteredWrongAnswer.split('~') : [];
    const noOfOptions = targets.length;

    const questionAnswerTime = startTime ? Math.floor((new Date().getTime() - startTime.getTime()) / 1000) : 0;

    const payload: QuestionPerformancePayload = {
      schoolCode: sessionStorage.getItem('SchoolCode') || '',
      familyCode: sessionStorage.getItem('FamilyCode') || '',
      login: sessionStorage.getItem('Login') || '',
      dicCode: sessionStorage.getItem('SelectedDICFile') || '',
      lsnTestCode: sessionStorage.getItem('LsnTestCode') || '',
      sessionId: sessionId || 1,
      qnNo: currentQnMeta?.QnNo,
      subNo: 0,
      qnType: currentQnMeta?.QnType,
      marks: marks,
      noOfOptions: noOfOptions,
      optOrder: buildOptionOrder(iframeRef.current, 'F'),
      enteredCorrectAnswer: enteredCorrect.join('|'),
      enteredWrongAnswer: enteredWrong.join('|'),
      enteredBlankAnswer: "",
      solutionSeen: (typeof solutionSeen === 'number' && solutionSeen > 0) || solutionSeen === 'Y' ? 'Y' : 'N',
      clueSeen: clueSeen,
      principleSeen: prcSeen,
      questionLoadTime: 0,
      questionAnswerTime: questionAnswerTime,
      lastQnInd: currentQnMeta?.LastQnInd || currentPage,
      nextQnData: buildNextQuestionData(lessonMeta?.qns, currentPage),
      dbgShuffledQnsOrder: buildShuffledQuestionsOrder(lessonMeta?.qns),
      allQuestionsCompleted: isCompleting ? 'True' : 'False',
      chapterCode: sessionStorage.getItem('ChapterCode') || '',
      unitCode: sessionStorage.getItem('UnitCode') || '',
      hwid: hwid,
      subject: subject
    };
const correctAnswer = payload.enteredCorrectAnswer
  .split('~')
  .map(item => item.substring(2))
  .join('~');

const wrongAnswer = payload.enteredWrongAnswer
  .split('~')
  .map(item => item.substring(2))
  .join('~');

console.log("&&&&&&&&&&C", correctAnswer);
console.log("&&&&&&&&&&W", wrongAnswer);

    const isLastQuestion = currentPage === totalPages;

    if (isLastQuestion) {
      setIsSubmitting(true);
      try {
        await handleDbUpdate(payload);
      } catch (e) {
        console.error("Final submit failed", e);
        showAlert("Error saving progress. Please try again.");
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
    } else {
      handleDbUpdate(payload).catch(e => console.error("Background save failed", e));
    }

    const CORRECT_URL = "https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsCorrect.GIF";

    targets.forEach((element, index) => {
      const el = element as HTMLElement;
      const userAnswer = config.userAnswers[el.id]?.trim() || '';
      const correctAnswer = correctAnswers[index]?.trim();
      const isEntryCorrect = userAnswer.toLowerCase() === correctAnswer.toLowerCase();

      let feedbackHtml = '';
      let borderStyle = 'border-bottom: 2px solid red;';

      if (isEntryCorrect) {
        feedbackHtml = `
            <span style="position: relative; display: inline-block;">
              <span style="color: green; font-family: Verdana, sans-serif;">${userAnswer}</span>
              <img src="${CORRECT_URL}" alt="Correct" style="vertical-align: middle;" />
            </span>`;
      } else {
        feedbackHtml = `
            <span style="color: red; text-decoration: line-through; font-family: Verdana, sans-serif;">${userAnswer}</span>
            <span style="color: green; margin-left: 0px; font-family: Verdana, sans-serif;">${correctAnswer}</span>`;
      }

      if (config.isInput) {
        const span = document.createElement('span');
        span.innerHTML = feedbackHtml;
        span.style.borderBottom = '2px solid red';
        span.style.display = 'inline-block';
        el.parentNode?.replaceChild(span, el);
      } else {
        el.className = '';
        el.style.cssText = `display: inline-block; ${borderStyle} background: transparent; padding: 0; margin: 0; width: auto; min-width: 0;`;
        el.innerHTML = feedbackHtml;
      }
    });

    const infoInputEl = iframeDoc.getElementById('KSD_MW_Info') as HTMLInputElement;
    let explanationHtml = "";

    if (infoInputEl && infoInputEl.value) {
      const parts = infoInputEl.value.split('|');
      if (marks === 1) {
        if (parts.length > 14 && parts[14]) explanationHtml = parts[14];
      } else {
        if (parts.length > 15 && parts[15]) explanationHtml = parts[15];
      }
    }

    if (explanationHtml && !iframeDoc.getElementById("el-explanation-container")) {
      const container = iframeDoc.createElement("div");
      container.id = "el-explanation-container";
      container.innerHTML = `
            <hr style='height:1px; background-color:#cfcfcf; border:none; margin-top:50px; margin-bottom:30px; margin-left:30px; margin-right:30px;'>
            <p style='font-size:24px; color:#797979; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:20px'>Explanation</p>
            <p style='font-size:24px; color:#000000; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:50px'>
                ${explanationHtml}
            </p>
        `;
      iframeDoc.body.appendChild(container);
      setTimeout(() => {
        iframeDoc.documentElement.scrollTop = iframeDoc.documentElement.scrollHeight;
      }, 100);
    }

    if (allCorrect) {
      playRandomSound(correctAudio, 'Correct');
    } else {
      playRandomSound(incorrectAudio, 'Incorrect');
      if (currentQnMeta?.NoOfSol && currentQnMeta.NoOfSol > 0) {
        handleSolnClick(true);
      }
    }

    setOkClicked(true);

  }, [answers, handleSolnClick, showAlert, isNumberPad, correctAudio, incorrectAudio, playRandomSound, startTime, lessonMeta, currentQnMeta, currentPage, totalPages, sessionId, isCompleting, handleDbUpdate, hwid, subject]);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.data?.type === "el-fillin-answers") setAnswers(event.data.value);
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  const handleQuestionFrameLoad = useIframeEnterKey(iframeRef, () => {
    iframeRef.current?.ownerDocument.getElementById('imgOK')?.click();
  });

  const handleBack = () => {
    const calledFrom = params.get("calledFrom");

    // console.log("calledFrom:", calledFrom);
    if (calledFrom) {
      const redirectPath = decodeURIComponent(calledFrom);
      window.location.href = redirectPath;
    }
    else if (window.history.length > 1) {
      window.history.back();
    }
  };

  return (
    <>
      {!isInAssessment && (
        <tr id="trQnButtons">
          <td className="QnPanelT">
            <table id="tabQnNav" className="QnPanel2T" style={{ height: "50px" }}>
              <tbody>
                <tr id="trQnOK" className="trQnOKT">
                  <td id="QnPanelIdT" className="QnPanel">&nbsp;</td>
                  <td id="QnPanelId2T" className="QnPanel">
                    <div
                      onClick={!okClicked ? handleBack : undefined}
                      style={{ display: okClicked ? 'none' : 'block', cursor: 'pointer' }}
                    >
                      <div id="imgbtnBack" className="imgbtnBackT">
                        <img
                          src={backSrc}
                          alt="Back"
                          onMouseOver={handleBackMouseOver}
                          onMouseOut={handleBackMouseOut}
                          onMouseDown={handleBackMouseDown}
                          onMouseUp={handleBackMouseUp}
                          className="imgbtnBack2T"
                        />
                      </div>
                    </div>
                  </td>
                  <td id="QnPanelId3T" className="QnPanel">
                    <input
                      type="image"
                      id="imgPrc"
                      src={principle_n}
                      className="imgPrcT"
                      alt="Principle"
                      style={{ display: !okClicked && currentQnMeta?.NoOfPrc && currentQnMeta.NoOfPrc > 0 ? 'inline-block' : 'none' }}
                      onClick={(e) => { e.preventDefault(); handlePrcClick(); }}
                    />
                  </td>
                  <td id="QnPanelId4T" className="QnPanel">
                    <input
                      type="image"
                      id="imgClue"
                      src={clue_n}
                      className="imgClueT"
                      alt="Clue"
                      style={{ display: !okClicked && Number(currentQnMeta?.NoOfClues || 0) > 0 ? 'inline-block' : 'none' }}
                      onClick={(e) => { e.preventDefault(); handleClueClick(); }}
                    />
                  </td>
                  <td id="QnPanelId5T" className="QnPanel">
                    <input
                      type="image" id="imgSoln" src={solutionSrc} className="imgSolnT" alt="Solution"
                      style={{ display: currentQnMeta?.NoOfSol && currentQnMeta.NoOfSol > 0 ? 'inline-block' : 'none' }}
                      onClick={(e) => { e.preventDefault(); handleSolnClick(false); }}
                      onMouseOver={handleSolutionMouseOver}
                      onMouseOut={handleSolutionMouseOut}
                      onMouseDown={handleSolutionMouseDown}
                    />
                  </td>
                  <td id="QnPanelId5T" className="QnPanel">

                    <a href={`/STUDENT/Tutorials/Tutorial?id=${folderParam}&name=${encodeURIComponent(lessonName ?? '')}&folder=${encodeURIComponent(diccode ?? '')}&calledFrom=${encodeURIComponent(calledFrom ?? '')}&sessionId=${sessionId}&currentPage=${currentPage}`}
                      id="imgbtnTut"
                      className="imgbtnTutT"
                      style={{ display: okClicked ? 'none' : 'inline-block' }}
                    >
                      <img src={tutorials_n} alt="Tutorial" id="imgbtnTut2T" />
                    </a>
                  </td>
                  <td id="QnPanelId6T" className="QnPanel">
                    {isSubmitting ? (
                      <Loading />
                    ) : okClicked ? (
                      currentPage === totalPages ? (
                        <img
                          src={doneSrc}
                          alt="Done"
                          style={{ verticalAlign: 'middle' }}
                          onClick={() => {

                            // View Lesson Preview
                            const isViewLesson = params.get("ViewLesson") === "Y";

                            if (isViewLesson) {
                              const calledFrom =
                                params.get("CalledFrom") ||
                                params.get("calledFrom");

                              if (calledFrom) {
                                window.location.href = decodeURIComponent(calledFrom);
                              } else {
                                window.history.back();
                              }
                              return;
                            }

                            // Student flow (unchanged)
                            const dicCode = sessionStorage.getItem('SelectedDICFile') || '';
                            const lsnTestCode = sessionStorage.getItem('LsnTestCode') || '';
                            const calledFrom = params.get("calledFrom") || "";

                            navigate(
                              `/STUDENT/Tutorials/Result?DicCode=${dicCode}&LsnTestCode=${lsnTestCode}&SessionID=${sessionId}&source=lesson` +
                              (calledFrom ? `&calledFrom=${encodeURIComponent(calledFrom)}` : "")
                            );
                          }}
                          onMouseOver={() => setDoneSrc(done_o)}
                          onMouseOut={() => setDoneSrc(done_n)}
                        />
                      ) : (
                        <img src={nextSrc} alt="Next" onClick={goToNextPage} onMouseOver={handleNextMouseOver} onMouseOut={handleNextMouseOut}
                          onMouseDown={handleNextMouseDown} onMouseUp={handleNextMouseUp} style={{ display: 'inline-block', cursor: 'pointer', marginTop: '3px' }} />
                      )
                    ) : (
                      <div onClick={handleOkClick} onMouseOver={handleOkMouseOver} onMouseOut={handleOkMouseOut}
                        onMouseDown={handleOkMouseDown} onMouseUp={handleOkMouseUp} style={{ display: 'inline-block', cursor: 'pointer' }}>
                        <img src={okSrc} alt="OK" style={{ marginTop: '5px' }} />
                      </div>
                    )}
                  </td>
                  <td id="QnPanelId7" className="QnPanel">&nbsp;</td>
                </tr>

                <tr id="trQnNext" className="trQnNextT">
                  <td id="tdBN1" className="QnPanel">&nbsp;</td>
                  <td id="tdAnsMsg" className="QnPanel">&nbsp;</td>
                  <td id="tdShowCA" className="QnPanel">&nbsp;</td>
                  <td id="tdAnsSoln" className="QnPanel">
                    <img id="imgSolnAfterAnswering" src={solutionSrc} alt="Solution" onMouseOver={handleSolutionMouseOver}
                      onMouseOut={handleSolutionMouseOut} onMouseDown={handleSolutionMouseDown} onClick={(e) => { e.preventDefault(); handleSolnClick(); }} />
                  </td>
                  <td id="tdQnNext" className="QnPanel">
                    {okClicked ? (
                      <input type="image" id="imgNextQuestion" src={nextSrc} alt="Next" onClick={goToNextPage}
                        onMouseOver={handleNextMouseOver} onMouseOut={handleNextMouseOut} onMouseDown={handleNextMouseDown}
                        onMouseUp={handleNextMouseUp} style={{ display: okClicked ? 'inline-block' : 'none' }} />
                    ) : (
                      <img id="imgOK" src={okSrc} alt="OK" onClick={handleOkClick} onMouseOver={handleOkMouseOver}
                        onMouseOut={handleOkMouseOut} onMouseDown={handleOkMouseDown} onMouseUp={handleOkMouseUp} className="imgOKT" style={{ display: okClicked ? 'none' : 'inline-block' }} />
                    )}
                  </td>
                  <td id="tdBN" className="QnPanel">&nbsp;</td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      )}

      <tr id="triFrame">
        <td id="tdFrameQnT" style={{ padding: 0, height: '100%' }}>
          {/* <div>yashu</div> */}
          <div id="divFrameQn" style={{ height: 'calc(95vh - 50px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
            {questionHtml ? (
              <iframe
                id="iFrameQn"
                ref={iframeRef}
                frameBorder={0}
                scrolling="yes"
                className="CssiFrm"
                style={{
                  flex: '1 1 0',
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  border: 'none',
                  overflow: 'auto'
                }}
                title="Question Page"
                onLoad={handleQuestionFrameLoad}
              />
            ) : <Loading />}
          </div>
        </td>
      </tr>

      {showClueModal && (
        <>
          {!showSolnModal && <div id="divReadOnlyScreen" style={{ display: 'block' }} onClick={() => setShowClueModal(false)}></div>}
          <div id="divClue" style={{ display: 'block' }}>
            <table id="tblTitleClue" className="clue" cellSpacing="0" cellPadding="0" width="100%" style={{ height: "50px", fontSize: "15px" }}>
              <tbody>
                <tr>
                  <td id="tdClueTitle1" align="left">Clue</td>
                  <td id="tdClueClose" align="right" valign="top">
                    <img
                      id="imgClueClose"
                      src={CloseBtnSrc}
                      alt="Close"
                      onClick={() => setShowClueModal(false)}
                      onMouseOver={handleCloseBtnMouseOver}
                      onMouseOut={handleCloseBtnMouseOut}
                      style={{ cursor: 'pointer', padding: '5px' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <div id="divFrameClue">
              {isClueLoading ? <Loading /> : (
                <iframe
                  id="iFrameClue"
                  ref={clueIframeRef}
                  title="Clue"
                  srcDoc={clueContent}
                  style={{ width: '100%', border: 'none', minHeight: '80px' }}
                  onLoad={() => {
                    // console.log("Clue iframe onLoad event fired.");
                    const iframe = clueIframeRef.current;
                    if (!iframe || !iframe.contentDocument || !iframe.contentWindow) { return; }
                    try {
                      const divShows = iframe.contentDocument.querySelectorAll("[id^='divShow']");
                      divShows.forEach(div => {
                        (div as HTMLElement).style.padding = "20px";
                      });
                      const cluesInput = iframe.contentDocument.getElementById('NoOfClues') as HTMLInputElement;
                      const totalClues = parseInt(cluesInput?.value || '0', 10);
                      setNoOfClues(totalClues);
                      const clueDivs = iframe.contentDocument.querySelectorAll<HTMLElement>('[id^="divShow"]');
                      clueDivs.forEach((div, index) => {
                        div.style.display = index === 0 ? 'block' : 'none';
                      });
                      const firstClueDiv = iframe.contentDocument.getElementById('divShow1');
                      if (firstClueDiv) {
                        const newHeight = firstClueDiv.scrollHeight;
                        iframe.style.height = `${Math.min(newHeight, 300)}px`;
                      } else if (totalClues === 0) {
                        iframe.style.height = '50px';
                      }
                    } catch (e) { console.error("Error inside clue iframe onLoad:", e); }
                  }}
                />
              )}
            </div>
            <table id="tblNavClue" className="clue" cellSpacing="0" cellPadding="0" width="100%" style={{ backgroundColor: '#F2F2F2' }}>
              <tbody>
                <tr>
                  <td style={{ width: '33%' }}>&nbsp;</td>
                  <td style={{ width: '34%' }}>&nbsp;</td>
                  <td align="right" style={{ width: '33%', paddingRight: '15px' }}>
                    {!isClueLoading && noOfClues > 1 && currentClueNo < noOfClues - 1 && (
                      <img
                        id="btnClueNext"
                        src={NextStepBtnSrc}
                        alt="Next Clue"
                        onClick={() => {
                          const nextClue = currentClueNo + 1;
                          const iframe = clueIframeRef.current;
                          if (iframe?.contentDocument) {
                            iframe.contentDocument.querySelectorAll<HTMLElement>('[id^="divShow"]').forEach((div, index) => {
                              div.style.display = index === nextClue ? 'block' : 'none';
                            });
                            setCurrentClueNo(nextClue);
                            const nextClueDiv = iframe.contentDocument.getElementById(`divShow${nextClue + 1}`);
                            if (nextClueDiv) {
                              const newHeight = nextClueDiv.scrollHeight + 20;
                              iframe.style.height = `${Math.min(newHeight, 300)}px`;
                            }
                          }
                        }}
                        onMouseOver={handleNextStepBtnMouseOver}
                        onMouseOut={handleNextStepBtnMouseOut}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                    {!isClueLoading && (noOfClues <= 1 || currentClueNo === noOfClues - 1) && (
                      <img
                        id="btnClueGotIt"
                        src={GotItBtnSrc}
                        alt="Got It"
                        onClick={() => setShowClueModal(false)}
                        onMouseOver={handleGotItBtnMouseOver}
                        onMouseOut={handleGotItBtnMouseOut}
                        style={{ cursor: 'pointer', display: isClueLoading ? 'none' : 'inline-block' }}
                      />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {showPrcModal && (
        <>
          <div id="divReadOnlyScreen" style={{ display: 'block' }} onClick={() => setShowPrcModal(false)}></div>

          <div id="divPCS" style={{ display: 'block' }} className="principle">

            {/* Header */}
            <table id="tblTitlePCS" className="principle" cellSpacing="0" cellPadding="0" width="100%">
              <tbody>
                <tr>
                  <td id="tdPCSTitle1" style={{ paddingLeft: '15px', color: 'white', fontWeight: 'bold', fontSize: '24px' }}>
                    Principle {principleList.length > 1 ? ` ${currentPrcIndex + 1} of ${principleList.length}` : ''}
                  </td>
                  <td id="tdPCSClose" align="right" valign="top">
                    <img
                      id="imgPCSClose"
                      src={CloseBtnSrc}
                      alt="Close"
                      onClick={() => { setShowPrcModal(false); setIsPrcLoading(false); }}
                      onMouseOver={handleCloseBtnMouseOver}
                      onMouseOut={handleCloseBtnMouseOut}
                      style={{ cursor: 'pointer', padding: '5px', width: '40px', height: '40px', marginRight: '30px' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Body */}
            <div id="divFramePCS">
              {isPrcLoading ? <Loading /> : (
                <iframe
                  id="iFramePrc"
                  ref={prcIframeRef}
                  title="Prc"
                  srcDoc={prcContent}
                  style={{ width: '100%', border: 'none', overflow: 'hidden', display: 'block' }} // 2. Set overflow hidden initially
                  onLoad={() => {
                    const iframe = prcIframeRef.current;
                    if (!iframe || !iframe.contentDocument || !iframe.contentWindow) return;

                    setTimeout(() => {
                      if (!iframe.contentDocument || !iframe.contentDocument.body) return;

                      const contentHeight = iframe.contentDocument.body.scrollHeight + 20;

                      const divPCS = document.getElementById('divPCS') as HTMLElement;
                      const titleTable = document.getElementById('tblTitlePCS') as HTMLElement;
                      const navTable = document.getElementById('tblNavPCS') as HTMLElement;
                      if (divPCS && titleTable && navTable) {
                        const maxModalHeight = window.innerHeight * 0.8;
                        const maxIframeHeight = maxModalHeight - titleTable.offsetHeight - navTable.offsetHeight;
                        if (contentHeight > maxIframeHeight) {

                          iframe.style.height = `${maxIframeHeight}px`;
                          iframe.style.overflowY = 'auto';
                        } else {

                          iframe.style.height = `${Math.max(contentHeight, 80)}px`;
                          iframe.style.overflowY = 'hidden';
                        }
                      } else {
                        iframe.style.height = `${Math.max(contentHeight, 80)}px`;
                      }
                    }, 50);
                  }}
                />
              )}
            </div>

            {/* Footer / Navigation */}
            <table id="tblNavPCS" className="principle" cellSpacing="0" cellPadding="0" width="100%" style={{ height: '50px' }}>
              <tbody>
                <tr style={{ backgroundColor: '#F2F2F2' }}>
                  {/* Previous Button */}
                  <td align="left" style={{ width: '33%', paddingLeft: '15px' }}>
                    {currentPrcIndex > 0 && (
                      <input
                        type='button'
                        value='PREVIOUS'
                        onClick={() => {
                          const prevIndex = currentPrcIndex - 1;
                          setCurrentPrcIndex(prevIndex);
                          const folderParam = new URLSearchParams(window.location.search).get('folder') || '';
                          const countryParam = params.get('country') || 'Australia';
                          fetchPrincipleContent(principleList[prevIndex], folderParam, countryParam).then(data => setPrcContent(data.principleHtml || ""));
                        }}
                        style={{
                          fontWeight: 'bold',
                          color: 'White',
                          fontSize: '12pt',
                          borderStyle: 'none',
                          backgroundColor: 'maroon',
                          cursor: 'pointer',
                          padding: '5px 10px'
                        }}
                      />
                    )}
                  </td>

                  <td style={{ width: '34%' }}>&nbsp;</td>

                  {/* Next / Got It Button */}
                  <td align="right" style={{ width: '33%', paddingRight: '15px' }}>
                    {currentPrcIndex < principleList.length - 1 ? (
                      <img
                        src={NextStepBtnSrc}
                        alt="Next"
                        onClick={() => {
                          const nextIndex = currentPrcIndex + 1;
                          setCurrentPrcIndex(nextIndex);
                          const folderParam = new URLSearchParams(window.location.search).get('folder') || '';
                          const countryParam = params.get('country') || 'Australia';
                          fetchPrincipleContent(principleList[nextIndex], folderParam, countryParam).then(data => setPrcContent(data.principleHtml || ""));
                        }}
                        onMouseOver={handleNextStepBtnMouseOver}
                        onMouseOut={handleNextStepBtnMouseOut}
                        style={{ cursor: 'pointer', width: '100px', height: '40px', marginRight: '30px', marginTop: '5px', marginBottom: '5px' }}
                      />
                    ) : (
                      <img
                        src={GotItBtnSrc}
                        id="imgGotIt"
                        alt="Got It"
                        onClick={() => { setShowPrcModal(false); setIsPrcLoading(false); }}
                        onMouseOver={handleGotItBtnMouseOver}
                        onMouseOut={handleGotItBtnMouseOut}
                        style={{ cursor: 'pointer', width: '100px', height: '40px', marginRight: '30px', marginTop: '5px', marginBottom: '5px' }}
                      />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {showSolnModal && (
        <>
          <div id="divReadOnlyScreen" style={{ display: 'block' }} onClick={() => {
            setShowSolnModal(false);
            setCurrentSolnNo(0);
          }}></div>

          <div id="divPCS" style={{ display: 'block', position: 'fixed', bottom: '0', width: '100%', zIndex: 10000 }}>
            <table id="tblTitlePCS" className="solution" cellSpacing="0" cellPadding="0" width="100%">
              <tbody>
                <tr>
                  <td id="tdPCSTitle1">Step by step solution</td>
                  <td id="tdPCSClose" align="right" valign="top">
                    <img
                      id="imgPCSClose"
                      src={CloseBtnSrc}
                      alt="Close"
                      onClick={() => {
                        setShowSolnModal(false);
                        setCurrentSolnNo(0);
                      }}
                      onMouseOver={handleCloseBtnMouseOver}
                      onMouseOut={handleCloseBtnMouseOut}
                      style={{ cursor: 'pointer', padding: '5px' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <div id="divFramePCS">
              {isSolnLoading ? <Loading /> : (
                <iframe
                  id="iFrameSolution"
                  ref={solnIframeRef}
                  title="Solution"
                  srcDoc={solnContent}
                  style={{ width: '100%', border: 'none', minHeight: '50px' }}
                  onLoad={() => {
                    const iframe = solnIframeRef.current;
                    if (!iframe || !iframe.contentDocument || !iframe.contentWindow) {
                      console.error("Iframe document or window not ready in onLoad.");
                      return;
                    }
                    try {
                      const iframeDoc = iframe.contentDocument;
                      const stepsInput = iframeDoc.getElementById('NoOfSteps') as HTMLInputElement;
                      const totalSteps = parseInt(stepsInput?.value || '0', 10);
                      setNoOfSteps(totalSteps);

                      const isAutoExpanded = iframeDoc.getElementById('IsAutoExpanded');

                      const stepRows = iframeDoc.querySelectorAll<HTMLElement>('[id^="trSolStepLHS"]');

                      stepRows.forEach((step, index) => {
                        if (index === 0 || isAutoExpanded) {
                          step.style.display = 'table-row';
                        } else {
                          step.style.display = 'none';
                        }
                      });

                      if (totalSteps === 0) {
                        iframe.style.height = '50px';
                      }

                      setTimeout(() => {
                        if (!iframe.contentDocument || !iframe.contentDocument.body) return;

                        const contentHeight = iframe.contentDocument.body.scrollHeight + 5;

                        const divPCS = document.getElementById('divPCS') as HTMLElement;
                        const titleTable = document.getElementById('tblTitlePCS') as HTMLElement;
                        const navTable = document.getElementById('tblNavPCS') as HTMLElement;

                        if (divPCS && titleTable && navTable) {
                          const maxModalHeight = window.innerHeight * 0.45;
                          const currentModalHeight = divPCS.offsetHeight;
                          const maxIframeHeight = maxModalHeight - titleTable.offsetHeight - navTable.offsetHeight;

                          if (contentHeight > maxIframeHeight) {
                            iframe.style.height = `${maxIframeHeight}px`;
                            iframe.style.overflowY = 'auto';
                          } else {
                            iframe.style.height = `${Math.max(contentHeight, 50)}px`;
                            iframe.style.overflowY = 'hidden';
                          }
                        }

                        if (isAutoExpanded) {
                          setCurrentSolnNo(totalSteps - 1);
                          if (solutionSeen !== 'Y') {
                            setSolutionSeen('Y');
                          }
                          iframe.contentWindow?.scrollTo(0, iframe.contentDocument.body.scrollHeight);
                        } else {
                          iframe.contentWindow?.scrollTo(0, 0);
                        }
                      }, 50);

                      iframeDoc.querySelectorAll<HTMLImageElement>('img[id^="KsdImg"]').forEach(img => {
                        img.onclick = () => {
                          const index = img.id.substring(6);
                          const ksdDiv = iframeDoc.getElementById(`KsdDiv${index}`);
                          const content = ksdDiv?.getAttribute('data-ksd-content');
                          const parentTd = img.closest('td');
                          if (content && parentTd && img.getAttribute('alt') === '1') {
                            parentTd.innerHTML = content;
                            img.setAttribute('alt', '2');
                          }
                        };
                      });

                    } catch (e) {
                      console.error("Error inside solution iframe onLoad:", e);
                    }
                  }}
                />
              )}
            </div>

            <table id="tblNavPCS" className="solution" cellSpacing="0" cellPadding="0" width="100%" style={{ backgroundColor: '#F2F2F2' }}>
              <tbody>
                <tr>
                  <td align="left" style={{ width: '33%', paddingLeft: '15px' }}>
                  </td>

                  <td align="center" style={{ width: '34%' }}>
                    &nbsp;
                  </td>

                  <td align="right" style={{ width: '33%', paddingRight: '15px' }}>
                    {!isSolnLoading && noOfSteps > 0 && currentSolnNo < noOfSteps - 1 && (
                      <img
                        id="btnPCSNext"
                        src={NextStepBtnSrc}
                        alt="Next Step"
                        onClick={() => {
                          const nextStep = currentSolnNo + 1;
                          const iframe = solnIframeRef.current;

                          if (iframe?.contentDocument?.body) {
                            const iframeDoc = iframe.contentDocument;

                            const stepRows = iframeDoc.querySelectorAll<HTMLElement>('[id^="trSolStepLHS"]');
                            if (nextStep >= stepRows.length) return;

                            const nextStepElement = stepRows[nextStep];
                            nextStepElement.style.display = 'table-row';

                            setTimeout(() => {
                              if (!iframe.contentDocument || !iframe.contentDocument.body) return;

                              const totalRequiredHeight = iframe.contentDocument.body.scrollHeight + 5;

                              const divPCS = document.getElementById('divPCS') as HTMLElement;
                              const titleTable = document.getElementById('tblTitlePCS') as HTMLElement;
                              const navTable = document.getElementById('tblNavPCS') as HTMLElement;

                              if (!divPCS || !titleTable || !navTable) return;

                              const maxModalHeight = window.innerHeight * 0.45;
                              const chromeHeight = titleTable.offsetHeight + navTable.offsetHeight;
                              const maxAllowedIframeHeight = maxModalHeight - chromeHeight;

                              if (totalRequiredHeight > maxAllowedIframeHeight) {
                                iframe.style.height = `${maxAllowedIframeHeight}px`;
                                iframe.style.overflowY = 'auto';
                              } else {
                                iframe.style.height = `${totalRequiredHeight}px`;
                                iframe.style.overflowY = 'hidden';
                              }

                              iframe.contentWindow?.scrollTo(0, iframe.contentDocument.body.scrollHeight);

                            }, 50);

                            setCurrentSolnNo(nextStep);
                            if (solutionSeen !== 'Y') {
                              setSolutionSeen(nextStep + 1);
                            }
                          }
                        }}
                        onMouseOver={handleNextStepBtnMouseOver}
                        onMouseOut={handleNextStepBtnMouseOut}
                        style={{ cursor: 'pointer' }}
                      />
                    )}

                    {!isSolnLoading && (noOfSteps === 0 || currentSolnNo === noOfSteps - 1) && (
                      <img
                        id="btnPCSClose"
                        src={GotItBtnSrc}
                        alt="Got It"
                        onClick={() => {
                          setShowSolnModal(false);
                          setCurrentSolnNo(0);
                          setSolutionSeen('Y');
                        }}
                        onMouseOver={handleGotItBtnMouseOver}
                        onMouseOut={handleGotItBtnMouseOut}
                        style={{ cursor: 'pointer', display: isSolnLoading ? 'none' : 'inline-block' }}
                      />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export default forwardRef(FQns);
